import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useLocation, useParams, useNavigate } from "react-router-dom";
import { axiosPrivate } from "../../api/axios";
import { 
  Box, 
  Button, 
  Card, 
  CardHeader, 
  CardContent, 
  useTheme, 
  Tabs, 
  Tab,
  Alert,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import MasterAgGrid from '../common/DataGrid/MasterAgGrid';
import ItemMasterDataDialog from './ItemMasterDataDialog';
import { useTranslation } from 'react-i18next';

const ItemMasterPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const gridRef = useRef();
  const theme = useTheme();
  const { group_code, project_code } = useParams();
  const [columnDefs, setColumnDefs] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [pageSize, setPageSize] = useState(100);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  
  const [tabs, setTabs] = useState([
    { id: 'items', name: t('pages.master.item.tabs.itemManagement') },
  ]);
  const [selectedTab, setSelectedTab] = useState('items');
  const [projectName, setProjectName] = useState('');
  const [dataDialogOpen, setDataDialogOpen] = useState(false);
  const [dataDialogMode, setDataDialogMode] = useState('add');
  const [currentData, setCurrentData] = useState(null);

  useEffect(() => {
    document.title = t('pages.master.item.title');
  }, [t]);
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await axiosPrivate.post('/master/items', {
        group_code,
        project_code
      });

      setColumnDefs([
        { field: 'item_id', headerName: t('pages.master.item.columns.id'), editable: false },
        { field: 'item_code', headerName: t('pages.master.item.columns.itemCode'), editable: false },
        { field: 'item_name', headerName: t('pages.master.item.columns.itemName'), editable: false },
        { field: 'item_type_code', headerName: t('pages.master.item.columns.itemType'), editable: false, 
          valueFormatter: params => {
            const typeMap = { 
              10: t('pages.master.item.itemTypes.finished'), 
              20: t('pages.master.item.itemTypes.semiFinished'), 
              30: t('pages.master.item.itemTypes.rawMaterial'), 
              40: t('pages.master.item.itemTypes.subsidiaryMaterial') 
            };
            return typeMap[params.value] || t('pages.master.item.itemTypes.unknown');
          }
        },
        { field: 'price', headerName: t('pages.master.item.columns.price'), editable: false },
        { field: 'description', headerName: t('pages.master.item.columns.description'), editable: false },
      ]);
      
      const dataWithId = response.data.map((row, index) => ({
        ...row,
        id: row.item_id || index + 1,
      }));
  
      setRowData(dataWithId);
    } catch (err) {
      console.error("데이터 조회 실패:", err);
      setError(t('pages.master.item.messages.loadError'));
    } finally {
      setLoading(false);
    }
  }, [group_code, project_code, t]);

  useEffect(() => {
      fetchData();
  }, [fetchData]);

  const handleAddRow = () => {
    setDataDialogMode('add');
    setCurrentData(null);
    setDataDialogOpen(true);
  };

  const handleDeleteRow = async () => {
    if (selectedRows.length === 0) return;
    
    if (window.confirm(t('pages.master.item.messages.deleteConfirm', { count: selectedRows.length }))) {
      setLoading(true);
      try {
        const ids = selectedRows.map(row => row.item_id);
        
        await axiosPrivate.post('/master/items/delete', {
          group_code,
          project_code,
          ids
        });
        
        await fetchData();
        setSelectedRows([]);
        alert(t('pages.master.item.messages.deleteSuccess', { count: ids.length }));
      } catch (err) {
        console.error("데이터 삭제 실패:", err);
        setError(t('pages.master.item.messages.deleteError'));
      } finally {
        setLoading(false);
      }
    }
  };

  const onSelectionChanged = () => {
    if (gridRef.current && gridRef.current.api) {
      const selectedNodes = gridRef.current.api.getSelectedNodes();
      const selectedData = selectedNodes.map(node => node.data);
      setSelectedRows(selectedData);
    }
  };

  const handleBackToProjects = () => {
    navigate('/projects');
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
  };

  const getPageTitle = () => {
    const tabName = t('pages.master.item.tabs.itemManagement');
    return projectName ? `${tabName} (${projectName})` : tabName;
  };

  const handleRowDoubleClicked = (params) => {
    setDataDialogMode('edit');
    setCurrentData(params.data);
    setDataDialogOpen(true);
  };

  const handleSaveData = async (data, mode) => {
    setLoading(true);
    try {
      const endpoint = mode === 'add' ? '/master/items/add' : '/master/items/update';
      await axiosPrivate.post(endpoint, {
        group_code,
        project_code,
        data
      });
      await fetchData();
      setDataDialogOpen(false);
      alert(mode === 'add' ? t('pages.master.item.messages.addSuccess') : t('pages.master.item.messages.editSuccess'));
    } catch (err) {
      console.error("데이터 저장 실패:", err);
      alert(t('pages.master.item.messages.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setDataDialogOpen(false);
  };

  return (
    <Grid container spacing={3} sx={{ height: "100%" }}>
      <Grid size={{ xs: 12 }} sx={{ height: "100%"}}>
        <Card sx={{
          height: "100%",
          display: 'flex',
          flexDirection: 'column',
          borderRadius: '0',
          bgcolor: theme.palette.mode === 'dark' 
            ? 'var(--mui-palette-background-default)' 
            : 'var(--mui-palette-background-paper)'
        }}>
          <CardHeader
            title={getPageTitle()}
            sx={{
              '& .MuiCardHeader-title': { fontSize: '22px', fontWeight: 600 },
              paddingBlock: '16px'
            }}
            action={
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackToProjects}
              >
                {t('pages.master.item.buttons.projectList')}
              </Button>
            }
          />
          
          <Box sx={{ px: 6 }}>
            <Tabs 
              value={selectedTab} 
              onChange={handleTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              sx={{ 
                '& .MuiTabs-indicator': { backgroundColor: theme.palette.primary.main },
                '& .MuiTab-root': {
                  textTransform: 'none',
                  minWidth: 'auto',
                  padding: '12px 16px',
                  fontWeight: 500,
                  color: theme.palette.text.secondary,
                  '&.Mui-selected': { color: theme.palette.primary.main },
                }
              }}
            >
              {tabs.map((tab) => (
                <Tab 
                  key={tab.id} 
                  value={tab.id} 
                  label={tab.name}
                  sx={{ 
                    minHeight: '48px',
                    '&.MuiButtonBase-root:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' }
                  }}
                />
              ))}
            </Tabs>
          </Box>
          
          {error && (
            <Box sx={{ px: 6, pt: 2 }}>
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            </Box>
          )}
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'flex-end',
            gap: 2,
            px: 6,
            pt: 2
          }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              disabled={loading}
            >
              {t('pages.master.item.buttons.add')}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteRow}
              disabled={selectedRows.length === 0 || loading}
            >
              {t('pages.master.item.buttons.delete')}
            </Button>
          </Box>

          <CardContent sx={{ 
            flexGrow: 1, 
            display: 'flex', 
            flexDirection: 'column',
            paddingInline: 6,
            paddingBlock: 2,
            height: 0
          }}>
            <MasterAgGrid
              gridRef={gridRef}
              rowData={rowData}
              columnDefs={columnDefs}
              onSelectionChanged={onSelectionChanged}
              pageSize={pageSize}
              loading={loading}
              onRowDoubleClicked={handleRowDoubleClicked}
            />
          </CardContent>
        </Card>
      </Grid>
      
      <ItemMasterDataDialog
        open={dataDialogOpen}
        onClose={handleCloseDialog}
        onSave={handleSaveData}
        data={currentData}
        mode={dataDialogMode}
      />
    </Grid>
  );
};

export default ItemMasterPage;